/**
 * textConverter — single-line TEXT (DTEXT) entity → `text-run` CadPrimitive.
 *
 * PDF-2 scope: standalone single-line TEXT (`type: "Text"`). The recorded
 * `text-run` shape is IDENTICAL to the one PDF-1 already emits for INSERT
 * ATTRIB (see `insertConverter.ts attributeToTextRun`), so the backend renders
 * both through one path.
 *
 * MTEXT multi-run rich formatting (`type: "MText"` inline codes / stacks /
 * paragraph layout) is DEFERRED to PDF-2b — `entityToPrimitives` does not route
 * standalone MTEXT here. (Block-child MTEXT is still recorded as a single run
 * by `insertConverter.textChildToRun`, matching PDF-1 behaviour.)
 *
 * Coordinates are RAW world mm (no applyOffset) — the PDF transform owns
 * near-origin localization (§4.1). The anchor is the TEXT `insertionPoint`,
 * except for justified text (hJustification / vJustification ≠ Left/Baseline)
 * where DXF stores the visual anchor in `alignmentPoint` (group 11/21). We
 * carry the resolved anchor so the backend can adjust it via font metrics.
 */

import { coerceColor, colorToLegacyString } from "@dxf-ai-editor/drawing-model";
import type { TextEntity } from "@dxf-ai-editor/drawing-model";

import { resolvePlotRgb } from "../pdfTransform";
import type {
  CadConvertContext,
  CadPrimitive,
  TextRunPrimitive,
} from "../cadPrimitive";
import { isDefaultOcs, ocsToWcs2d } from "./ocs";
import { resolvePlotStyle } from "./resolveStyle";

/**
 * DXF horizontal justification (group 72) values where the visual anchor is
 * NOT the insertion point but the alignment point (11/21):
 *   0 = Left (uses insertionPoint), 1 = Center, 2 = Right, 3 = Aligned,
 *   4 = Middle, 5 = Fit.
 */
function usesAlignmentPoint(hJust: number, vJust: number): boolean {
  // Any non-left horizontal justification, OR any non-baseline vertical
  // justification, stores the visual anchor in the alignment point. DXF places
  // the alignment point at code 11/21 when either is non-default.
  return hJust !== 0 || vJust !== 0;
}

/**
 * Convert a single-line TEXT entity to its recorded `text-run` primitive.
 *
 * The run carries content / height / rotation / widthFactor / obliqueAngle /
 * justification / style name + resolved plot colour. Empty / whitespace-only
 * content still produces a run (AutoCAD keeps zero-width TEXT slots; the
 * backend simply emits nothing visible for an empty string).
 */
export function textToPrimitives(
  entity: TextEntity,
  ctx: CadConvertContext,
): CadPrimitive[] {
  // DXF stores the visual anchor in `alignmentPoint` (11/21) for any justified
  // text; the `insertionPoint` (10/20) is then the baseline-left reference.
  // We resolve the anchor here so the backend's alignment math has a single,
  // canonical position to adjust from.
  const anchorSource =
    usesAlignmentPoint(entity.hJustification, entity.vJustification) &&
    entity.alignmentPoint !== undefined
      ? entity.alignmentPoint
      : entity.insertionPoint;

  // PDF-5d OCS/extrusion: TEXT carries an `extrusion` (OCS normal). When
  // non-default, lift its anchor (insertion / alignment point, both stored in
  // the OCS) to WCS XY before recording the run. Default (0,0,1) → identity
  // (isDefaultOcs short-circuits), so existing text tests stay unchanged.
  const anchor = isDefaultOcs(entity.extrusion)
    ? { x: anchorSource.x, y: anchorSource.y }
    : ocsToWcs2d(entity.extrusion)(anchorSource.x, anchorSource.y);

  // PDF-6.3b — resolve the plot style so CTB/STB overrides (colour, alpha)
  // and do-not-plot apply to text too. The base colour comes from the style
  // (which already folded the override in resolveStyle) instead of
  // `resolvePlotRgb` — though we keep the latter as a fallback when there is
  // no table loaded (resolvePlotStyle's colour resolution is identical when
  // no override applies, so the produced RGB matches byte-for-byte).
  const style = resolvePlotStyle(entity, ctx);
  if (style.skip) return [];
  const color = ctx.plotStyleTable
    ? style.color
    : resolvePlotRgb(
        // EPIC-COLOR-UNION S1 — project the typed union to the legacy plot string.
        colorToLegacyString(coerceColor(entity.color)),
        entity.layer,
        ctx.layerColors,
        ctx.blockColor,
      );

  const run: TextRunPrimitive = {
    type: "text-run",
    entityId: entity.id,
    layer: entity.layer ?? "0",
    position: anchor,
    height: entity.height,
    rotation: entity.rotation,
    widthFactor: entity.widthFactor,
    obliqueAngle: entity.obliqueAngle,
    hJustification: entity.hJustification,
    vJustification: entity.vJustification,
    styleName: entity.styleName,
    value: entity.contents,
    color,
    ...(style.alpha !== undefined ? { alpha: style.alpha } : {}),
  };
  return [run];
}
